import React, { useState } from "react";
import {
  Menu,
  MenuItem,
  Popover,
  Button,
  Checkbox,
  Tooltip,
  Box,
  Card,
  withStyles,
  Grid,
  Typography
} from "@material-ui/core";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import { DateRange } from "react-date-range";
import { addDays, addMonths, differenceInDays, format, subDays } from "date-fns";
import { es, enUS, pt } from "react-date-range/src/locale/index.js";
import {
  APPLY_GLOBAL_PERIOD,
  SELECTED_GLOBAL_PERIOD,
} from "../../utils/storageNames";
import { getDefaultPeriod } from "../../utils/local-storage/select-periodo";
import i18n from "../../i18n";
import { useTranslation } from "react-i18next";

const styles = {
  customSelect: {
    width: "270px",
    borderStyle: "solid",
    borderColor: "rgb(192, 192, 192)",
    borderWidth: 1,
    paddingLeft: "14px",
    fontSize: "14px",
    color: "rgb(102, 120, 138)",
    cursor: "pointer",
    height: "37.625px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  textContainer: { whiteSpace: "nowrap", overflow: "hidden" },
  iconContainer: {
    width: "35px",
    display: "flex",
    justifyContent: "center",
  },
};

const locales = { es: es, en: enUS, pt: pt };

const defaultPeriods = (t) => ([
  {
    value: "hora",
    text: "Última hora",
    typePeriod: 0,
    period: "h",
  },
  {
    value: "3hora",
    text: "Últimas 3 horas",
    typePeriod: 4,
    period: "3h",
  },
  {
    value: "dia",
    text: "Último día",
    typePeriod: 1,
    period: "d",
  },
  {
    value: "3dia",
    text: "Últimos 3 días",
    typePeriod: 5,
    period: "3d",
  },
  {
    value: "semana",
    text: "Última semana",
    typePeriod: 2,
    period: "w",
  },
]);

export const defaultSelectDateRange = {
  startDate: new Date(),
  endDate: new Date(),
  key: "selection",
};

function AtSelectPeriod(props) {
  const { changePeriodFn, classes, persistInStorage, hideApplyToAll } = props;
  const { t } = useTranslation();
  const periods = props.defaultPeriods ?? defaultPeriods(t);
  const {
    appliedToAllSelected,
    parseSelectedGlobalPeriod,
  } = getDefaultPeriod();

  const selectedGlobalPeriodValidate =
    persistInStorage && parseSelectedGlobalPeriod
      ? parseSelectedGlobalPeriod
      : props.activePeriodFromParent ? props.activePeriodFromParent : periods[0];

  const [anchorEl, setAnchorEl] = useState(null);
  const [inputText, setInputText] = useState(selectedGlobalPeriodValidate.text);
  const [applyToAll, setApplyToAll] = useState(appliedToAllSelected);
  const [calendarAnchorEl, setCalendarAnchorEl] = useState(null);
  const [dateRange, setDateRange] = useState(
    selectedGlobalPeriodValidate.statePeriod || defaultSelectDateRange
  );
  const [activePeriod, setActivePeriod] = useState(
    selectedGlobalPeriodValidate.value
  );

  function handleClick(event) {
    setAnchorEl(event.currentTarget);
  }

  function handleCloseMenu() {
    setAnchorEl(null);
  }

  function handleChangePeriod({ text, value, period }) {
    setInputText(text);
    setAnchorEl(null);
    setActivePeriod(value);
    const savePeriod = { value, statePeriod: dateRange, text, period };
    if (applyToAll || hideApplyToAll) changePeriodFn(savePeriod);
    if (persistInStorage) {
      localStorage.setItem(SELECTED_GLOBAL_PERIOD, JSON.stringify(savePeriod));
    }
  }

  function handleClickCustom(event) {
    setCalendarAnchorEl(event.currentTarget);
  }

  function handleChangeCustomPeriod(range) {
    const selectedDateRange = range.selection;
    if (props.limitDays && props.limitDays > 0 && differenceInDays(selectedDateRange.endDate.getTime(), selectedDateRange.startDate.getTime()) > props.limitDays) {      
      selectedDateRange.startDate = subDays(selectedDateRange.endDate, props.limitDays);
    }
    setDateRange(selectedDateRange);
  }

  function handleCloseSelectDateRange() {
    setCalendarAnchorEl(null);
  }

  function handleGenerateRange() {
    const text = `Custom: ${format(
      dateRange.startDate,
      t('date-formats.shortDate')
    )} - ${format(dateRange.endDate, t('date-formats.shortDate'))}`;
    setInputText(text);
    setCalendarAnchorEl(null);
    setAnchorEl(null);
    setActivePeriod("custom");
    const period = {
      value: "custom",
      statePeriod: dateRange,
      text,
      period: "c",
    };
    if (applyToAll || hideApplyToAll) changePeriodFn(period);
    if (persistInStorage) {
      localStorage.setItem(SELECTED_GLOBAL_PERIOD, JSON.stringify(period));
    }
  }

  function handleChangeApplyToAll(event) {
    const newCheckStatus = event.target.checked;
    setApplyToAll(newCheckStatus);
    if (newCheckStatus) {
      changePeriodFn({ value: activePeriod, statePeriod: dateRange });
    }
    if (persistInStorage) {
      localStorage.setItem(APPLY_GLOBAL_PERIOD, newCheckStatus);
    }
  }

  return (
    <Box display="flex" justifyContent="end">
      {!hideApplyToAll && (
        <>
          <Grid style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Grid item style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <label
                style={{
                  textAlign: "center",
                  color: "#6b6b6b",
                  fontFamily: "Roboto",
                }}
              >
                {t('at-select-period.sync-panels')}
              </label>
            </Grid>
            <Grid item style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Tooltip placement="bottom" title={t('at-select-period.apply-all')}>
                <Checkbox
                  inputProps={{ "aria-label": "primary checkbox" }}
                  checked={applyToAll}
                  onChange={handleChangeApplyToAll}
                  color="primary"
                  style={{ marginRight: "15px" }}
                />
              </Tooltip>
            </Grid>
          </Grid>
        </>
      )}
      <Card
        className={classes.customSelect}
        elevation={0}
        onClick={handleClick}
      >
        <div className={classes.textContainer}>{t(`period.select.option.${inputText}`)}</div>
        <div className={classes.iconContainer}>
          <ArrowDropDownIcon />
        </div>
      </Card>
      <Menu
        id="period-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        {periods.map((period) => (
          <MenuItem
            key={period.value}
            value={period.value}
            onClick={() => handleChangePeriod(period)}
          >
            {t(`period.select.option.${period.text}`)}
          </MenuItem>
        ))}
        <MenuItem value="custom" onClick={handleClickCustom}>
          Custom
        </MenuItem>
      </Menu>
      <Popover
        open={Boolean(calendarAnchorEl)}
        anchorEl={calendarAnchorEl}
        onClose={handleCloseSelectDateRange}
      >
        <div>
          {
            props.limitDays && props.limitDays > 0 ?
              <Box padding={1}>
                <Typography variant='caption' style={{color: '#c26b13'}}>{t('at-select-period.limit-days-msg', { maxDays: props.limitDays })}</Typography>
              </Box>
            : null
          }
          <DateRange
            maxDate={ props.customMaxDate || new Date() }
            minDate={ props.customMinDate || addMonths(new Date(), -6) }
            locale={locales[i18n.language] || locales['en']}
            onChange={handleChangeCustomPeriod}
            moveRangeOnFirstSelection={false}
            ranges={[dateRange]}
            className={"PreviewArea"}
          />
        </div>
        <div style={{ display: "flex", justifyContent: "end" }}>
          <Button onClick={handleGenerateRange}>{t('at-select-period.custom-button-submit')}</Button>
        </div>
      </Popover>
    </Box>
  );
}

export default withStyles(styles)(AtSelectPeriod);
